import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import FadeUp from "../components/FadeUp";
import { useLang } from "../i18n";
import { api } from "../lib/api";

const HERO_IMG = "https://images.unsplash.com/photo-1622277430358-f4d134452e2e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHx3ZWRkaW5nJTIwZ293biUyMGJyaWRlfGVufDB8fHx8MTc5MzE5NDA1Mnww&ixlib=rb-4.1.0&q=85";
const PORTRAIT_1 = "https://images.unsplash.com/photo-1492175742197-ed20dc5a6bed?crop=entropy&cs=srgb&fm=jpg&q=85";
const ATELIER_IMG = "https://images.unsplash.com/photo-1769107805465-bfd41863f1a0?crop=entropy&cs=srgb&fm=jpg&q=85";
const QUOTE_IMG = "/quote-img.jpeg";

const Marquee = () => {
  const { t } = useLang();
  const text = t("marquee.items");
  return (
    <div className="overflow-hidden border-y border-softline bg-sand py-5" data-testid="home-marquee">
      <div className="marquee-track">
        {[0, 1].map((half) => (
          <span key={half} className="font-serif italic text-2xl sm:text-3xl text-charcoal/40 whitespace-nowrap pr-16">{text}</span>
        ))}
      </div>
    </div>
  );
};

const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  const lines = [t("hero.line1"), t("hero.line2"), t("hero.line3")];
  return (
    <section ref={ref} className="relative min-h-screen flex items-end lg:items-center" data-testid="home-hero">
      <div className="absolute inset-0 lg:left-[38%] clip-frame">
        <motion.img src={HERO_IMG} alt="Abito da sposa Per Lei Bridal" className="w-full h-full object-cover spotlight-img"
          style={{ y: imgY, scale: imgScale }} />
        <div className="absolute inset-0 bg-gradient-to-t from-ivory via-ivory/85 to-ivory/25 lg:bg-gradient-to-r lg:from-ivory lg:via-ivory/30 lg:to-transparent" />
      </div>
      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12 w-full pb-24 lg:py-0">
        <p className="eyebrow mb-8" style={{ animation: "none" }}>
          <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }}>{t("hero.eyebrow")}</motion.span>
        </p>
        <h1 className="font-serif font-light text-[13vw] lg:text-[7.2vw] leading-[0.98] text-charcoal max-w-4xl">
          {lines.map((line, i) => (
            <span key={i} className="mask-line">
              <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }}
                transition={{ delay: 0.35 + i * 0.16, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}>
                {i === 1 ? <em className="italic text-gold">{line}</em> : line}
              </motion.span>
            </span>
          ))}
        </h1>
        <motion.p className="mt-8 max-w-md text-[15px] leading-relaxed text-stone"
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.9 }}>
          {t("hero.sub")}
        </motion.p>
        <motion.div className="mt-10 flex flex-wrap gap-6 items-center"
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.25, duration: 0.9 }}>
          <Link to="/collezione" className="btn-solid" data-testid="hero-cta-collection">{t("hero.cta")}</Link>
          <Link to="/appuntamenti" className="btn-line" data-testid="hero-cta-appointment">{t("hero.cta2")}</Link>
        </motion.div>
      </div>
    </section>
  );
};

const Services = () => {
  const { t } = useLang();
  return (
    <section className="max-w-[1440px] mx-auto px-6 lg:px-12 py-24 lg:py-36" data-testid="home-services">
      <div className="grid gap-14 lg:grid-cols-12 items-start">
        <div className="lg:col-span-5">
          <FadeUp><p className="eyebrow mb-6">{t("home.servicesKicker")}</p></FadeUp>
          <FadeUp delay={0.1}>
            <h2 className="font-serif font-light text-4xl sm:text-5xl leading-[1.08] max-w-xl">{t("home.servicesTitle")}</h2>
          </FadeUp>
          <FadeUp delay={0.2}>
            <Link to="/noleggio" className="btn-line mt-10 inline-flex" data-testid="home-services-cta">{t("home.viewAll")}</Link>
          </FadeUp>
        </div>
        <div className="lg:col-span-7 grid gap-8 sm:grid-cols-2">
          <FadeUp delay={0.1}>
            <article className="bg-warm p-8 h-full">
              <p className="eyebrow mb-4">{t("home.servicesRental")}</p>
              <p className="text-[15px] leading-relaxed text-stone">{t("home.servicesRentalBody")}</p>
            </article>
          </FadeUp>
          <FadeUp delay={0.2}>
            <article className="bg-warm p-8 h-full">
              <p className="eyebrow mb-4">{t("home.servicesMto")}</p>
              <p className="text-[15px] leading-relaxed text-stone">{t("home.servicesMtoBody")}</p>
            </article>
          </FadeUp>
        </div>
      </div>
    </section>
  );
};

const Destination = () => {
  const { t } = useLang();
  return (
    <section className="bg-warm py-24 lg:py-32" data-testid="home-destination">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 grid gap-12 lg:grid-cols-12 items-center">
        <FadeUp className="lg:col-span-5">
          <div className="clip-frame aspect-[3/4]">
            <img src={PORTRAIT_1} alt="Destination bridal" className="w-full h-full object-cover spotlight-img" loading="lazy" />
          </div>
        </FadeUp>
        <div className="lg:col-span-7 lg:pl-16">
          <FadeUp><p className="eyebrow mb-6">{t("home.destinationKicker")}</p></FadeUp>
          <FadeUp delay={0.1}>
            <h2 className="font-serif font-light text-4xl sm:text-5xl leading-[1.08] max-w-xl">{t("home.destinationTitle")}</h2>
          </FadeUp>
          <FadeUp delay={0.2}>
            <p className="mt-8 max-w-lg text-[15px] leading-relaxed text-stone">{t("home.destinationBody")}</p>
          </FadeUp>
          <FadeUp delay={0.3}>
            <Link to="/noleggio" className="btn-line mt-10 inline-flex" data-testid="home-destination-cta">{t("nav.rental")}</Link>
          </FadeUp>
        </div>
      </div>
    </section>
  );
};

const AtelierTeaser = () => {
  const { t } = useLang();
  return (
    <section className="max-w-[1440px] mx-auto px-6 lg:px-12 py-24 lg:py-36" data-testid="home-atelier-teaser">
      <div className="grid gap-14 lg:grid-cols-12 items-start">
        <div className="lg:col-span-5">
          <FadeUp><p className="eyebrow mb-6">{t("home.atelierKicker")}</p></FadeUp>
          <FadeUp delay={0.1}>
            <h2 className="font-serif font-light text-4xl sm:text-5xl leading-[1.08] max-w-xl">{t("home.atelierTitle")}</h2>
          </FadeUp>
          <FadeUp delay={0.2}>
            <p className="mt-8 max-w-lg text-[15px] leading-relaxed text-stone">{t("home.atelierBody")}</p>
          </FadeUp>
          <FadeUp delay={0.3}>
            <Link to="/atelier" className="btn-line mt-10 inline-flex" data-testid="home-atelier-cta">{t("nav.atelier")}</Link>
          </FadeUp>
        </div>
        <FadeUp className="lg:col-span-7 lg:pl-16">
          <div className="grid gap-10 grid-cols-2 items-start">
            <div className="clip-frame aspect-[3/4] max-w-xs">
              <img src={QUOTE_IMG} alt="Prova abito" className="w-full h-full object-cover spotlight-img" loading="lazy" />
            </div>
            <div className="pt-12">
              <p className="font-serif italic text-2xl text-charcoal/80 leading-snug">“{t("home.bookTeaser")}”</p>
              <p className="eyebrow mt-5">Per Lei Bridal, Milano</p>
            </div>
          </div>
        </FadeUp>
      </div>
    </section>
  );
};

const AppointmentTeaser = () => {
  const { t } = useLang();
  return (
    <section className="bg-charcoal text-ivory py-24 lg:py-32" data-testid="home-appointment-teaser">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 grid gap-10 lg:grid-cols-2 items-center">
        <FadeUp>
          <p className="eyebrow !text-ivory/50 mb-6">{t("home.appointmentKicker")}</p>
          <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">{t("home.appointmentTitle")}</h2>
          <p className="mt-6 max-w-md text-ivory/60 text-[15px] leading-relaxed">{t("home.appointmentBody")}</p>
        </FadeUp>
        <FadeUp delay={0.15} className="lg:pl-16">
          <Link to="/appuntamenti" className="btn-solid !bg-ivory !text-charcoal hover:!bg-gold" data-testid="home-appointment-cta">{t("nav.book")}</Link>
        </FadeUp>
      </div>
    </section>
  );
};

const Featured = () => {
  const { t, lang } = useLang();
  const [dresses, setDresses] = useState([]);
  useEffect(() => {
    api.get("/dresses", { params: { featured: true } })
      .then((r) => setDresses(r.data.slice(0, 4)))
      .catch(() => {});
  }, []);
  return (
    <section className="max-w-[1440px] mx-auto px-6 lg:px-12 py-24 lg:py-32" data-testid="home-featured">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
        <div>
          <FadeUp><p className="eyebrow mb-4">{t("home.featuredKicker")}</p></FadeUp>
          <FadeUp delay={0.1}><h2 className="font-serif font-light text-4xl sm:text-5xl">{t("home.featuredTitle")}</h2></FadeUp>
        </div>
        <FadeUp delay={0.2}><Link to="/collezione" className="btn-line" data-testid="home-featured-viewall">{t("home.viewAll")}</Link></FadeUp>
      </div>
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {dresses.map((d, i) => (
          <FadeUp key={d.id} delay={i * 0.08}>
            <Link to={`/collezione/${d.id}`} data-testid={`featured-dress-${d.code}`} className="block group">
              <div className="clip-frame aspect-[3/4] border border-softline">
                <img src={d.images?.[0]} alt={d.name} className="w-full h-full object-cover spotlight-img" loading="lazy"
                  onError={(e) => { e.currentTarget.style.display = "none"; }} />
              </div>
              <div className="pt-4">
                <p className="font-serif text-xl">{d.name}</p>
                <p className="text-[11px] tracking-[0.18em] uppercase text-muted mt-1">{d.fabric}</p>
              </div>
            </Link>
          </FadeUp>
        ))}
      </div>
    </section>
  );
};

export default function Home() {
  return (
    <>
      <Hero />
      <Marquee />
      <AtelierTeaser />
      <Services />
      <Featured />
      <Destination />
      <AppointmentTeaser />
    </>
  );
}
